// src/pages/projects/RetailPricingOptimization.jsx
import React from "react";
import { ProjectLayout } from "../../components/layout/ProjectLayout";
import "../../components/styles/caseStudy.css";

const Stat = ({ value, label }) => (
  <div className="cs_stat">
    <span className="cs_stat_value">{value}</span>
    <span className="cs_stat_label">{label}</span>
  </div>
);

const Finding = ({ number, question, finding }) => (
  <div className="cs_finding">
    <span className="cs_finding_number">{number}</span>
    <div className="cs_finding_body">
      <p className="cs_finding_question">{question}</p>
      <p className="cs_finding_answer">{finding}</p>
    </div>
  </div>
);

const RetailPricingOptimization = () => {
  return (
    <ProjectLayout
      title="Retail Pricing Optimization Analysis"
      context="9,994 US retail transactions across three categories analyzed to identify margin leaks, quantify the impact of discounting behavior, and deliver dollar-quantified pricing recommendations."
      tools={["SQL", "SQLite", "Google Sheets", "Power BI"]}
      repoUrl="https://github.com/abranco0403/retail-pricing-optimization"
    >
      <div className="cs_content">

        {/* ── Stats Row ── */}
        <div className="cs_stats_row">
          <Stat value="9,994" label="Transactions analyzed" />
          <Stat value="4 yrs" label="Data period (2014–2017)" />
          <Stat value="$234K" label="Addressable margin opportunity" />
          <Stat value="3" label="Unprofitable sub-categories identified" />
        </div>

        {/* ── Business Questions ── */}
        <div className="cs_section">
          <h2 className="cs_section_title">Business Questions</h2>
          <ul className="cs_list">
            <li>Revenue is growing — why is margin not keeping pace?</li>
            <li>Which categories and sub-categories are structurally unprofitable?</li>
            <li>At what discount level does profit turn negative — and how much is that costing us?</li>
            <li>What specific pricing actions would recover the most margin?</li>
          </ul>
        </div>

        {/* ── Approach ── */}
        <div className="cs_section">
          <h2 className="cs_section_title">Approach</h2>
          <ol className="cs_list cs_list--ordered">
            <li>Defined the business problem and stated hypotheses before touching the data.</li>
            <li>Audited the dataset — confirmed zero nulls, flagged 18.7% negative-profit rows and discount rates reaching 80%.</li>
            <li>Reframed elasticity as discount sensitivity since price per SKU is fixed in the dataset.</li>
            <li>Wrote 6 SQL queries to diagnose margin by category, discount band impact, sub-category ranking, and year-over-year trend.</li>
            <li>Built a Google Sheets model with pivot tables and charts translating SQL findings into a CFO-ready visual output.</li>
            <li>Quantified dollar impact for each recommendation with stated assumptions and honest caveats.</li>
          </ol>
        </div>

        {/* ── Key Findings ── */}
        <div className="cs_section">
          <h2 className="cs_section_title">Key Findings</h2>
          <div className="cs_findings">
            <Finding
              number="01"
              question="Which category is the primary margin leak?"
              finding="Furniture generates 32% of revenue but earns only 2.5% margin — vs 17% for both Office Supplies and Technology. Furniture earns 7x less profit than its peers on nearly the same revenue base. The gap is not explained by cost structure alone — Furniture averages 17.4% discount, higher than both peer categories."
            />
            <Finding
              number="02"
              question="At what discount level does profit turn negative?"
              finding="The cliff is at 20%. Transactions with 0% discount average $67 profit. Transactions with 1–20% discount average $27 profit. Above 20%, 92% of transactions lose money. Above 50%, every single transaction loses money — without exception. This is a policy problem, not a market problem."
            />
            <Finding
              number="03"
              question="Which sub-categories are structurally unprofitable?"
              finding="Tables loses $17,725 total at −8.6% margin despite $207K in revenue. Bookcases loses $3,473 at −3.0% margin. Supplies loses $1,189 at −2.5% margin. The root cause for Tables is discounting — at zero discount, Tables averages $184 profit per transaction. With any discount applied, average profit drops to −$126. The product is viable. The discount policy is not."
            />
            <Finding
              number="04"
              question="Is the margin problem getting worse over time?"
              finding="Revenue grew 51% from 2014 to 2017. Margin peaked at 13.4% in 2016 and declined to 12.7% in 2017 — the highest revenue year on record. Without intervention, margin erosion accelerates as volume grows. The problem is structural, not cyclical."
            />
          </div>
        </div>

        {/* ── Recommendations ── */}
        <div className="cs_section">
          <h2 className="cs_section_title">Recommendations</h2>
          <div className="cs_table_wrapper">
            <table className="cs_table">
              <thead>
                <tr>
                  <th>Recommendation</th>
                  <th>Action</th>
                  <th>Est. Impact</th>
                  <th>Priority</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Cap discounts at 20%</td>
                  <td>Enforce hard cap policy — require leadership approval above 20%</td>
                  <td>+$178K profit</td>
                  <td>High</td>
                </tr>
                <tr>
                  <td>Fix Tables discounting</td>
                  <td>Reduce average discount to peer level (17% vs current 26%)</td>
                  <td>+$17K profit</td>
                  <td>High</td>
                </tr>
                <tr>
                  <td>Protect high-margin stars</td>
                  <td>No discount increases on Labels (44%), Paper (43%), Copiers (37%)</td>
                  <td>$3.9K+/yr</td>
                  <td>Medium</td>
                </tr>
                <tr>
                  <td>Set Furniture margin floor</td>
                  <td>Minimum 10% margin target — flag any Furniture deal below threshold</td>
                  <td>+$55K profit</td>
                  <td>Medium</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="cs_body_text" style={{ marginTop: "12px", fontStyle: "italic", opacity: 0.7 }}>
            Total addressable opportunity: ~$234K over 4 years (~$58K/year). 
            Estimates assume volume holds. Some discounts may be competitively necessary and would require individual review.
          </p>
        </div>

        {/* ── Summary ── */}
        <div className="cs_section">
          <h2 className="cs_section_title">Summary</h2>
          <div className="cs_table_wrapper">
            <table className="cs_table">
              <thead>
                <tr>
                  <th>Question</th>
                  <th>Finding</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Primary margin leak</td>
                  <td>Furniture earns 2.5% margin on 32% of revenue — 7x below peers</td>
                </tr>
                <tr>
                  <td>Discount break point</td>
                  <td>Above 20% discount, 92% of transactions average a net loss</td>
                </tr>
                <tr>
                  <td>Worst sub-category</td>
                  <td>Tables loses $17,725 — discount policy, not product cost, is the cause</td>
                </tr>
                <tr>
                  <td>Trend direction</td>
                  <td>Margin peaked in 2016 and is declining as revenue grows</td>
                </tr>
                <tr>
                  <td>Total opportunity</td>
                  <td>~$234K over 4 years from 4 targeted pricing actions</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

      </div>
    </ProjectLayout>
  );
};

export default RetailPricingOptimization;